/**
 * Site Settings – Shared reactive store
 * ======================================
 * Loads /api/settings/ once and exposes data to every component.
 *
 * Usage in any component:
 *   import { siteSettings, loadSettings } from '@/services/settings';
 *   // in created(): await loadSettings();
 *   // in template/script: siteSettings.logo, siteSettings.school_name, etc.
 */

import { reactive } from 'vue';
import api from './api';
import { mediaUrl } from './media';

export const siteSettings = reactive({
  loaded: false,
  school_name: 'Al Shola International School',
  school_name_ar: 'مدرسة الشعلة الأمريكية',
  logo: '',
  logo_secondary: '',
  favicon: '',
  address_line_1: 'Almwihat Street',
  address_line_2: 'Al-Talla 2',
  city: 'Ajman, U.A.E.',
  address: '',
  phone_primary: '+971 54 7041499',
  phone_secondary: '+971 6 7451110',
  phone: '',
  email: '',
  google_maps_embed_url: '',
  map_embed_url: '',
  facebook_url: '',
  twitter_url: '',
  instagram_url: '',
  youtube_url: '',
  parent_portal_url: '',
  school_portal_url: '',
  virtual_tour_url: '',
  footer_text: '',
  copyright_text: '',
});

let _promise = null;

export async function loadSettings() {
  // Only load once
  if (siteSettings.loaded) return siteSettings;
  if (_promise) return _promise;

  _promise = (async () => {
    try {
      const data = await api.getSettings();
      if (data) {
        // Image fields that need mediaUrl resolution
        const imageFields = ['logo', 'logo_secondary', 'favicon'];

        // Merge all fields from API response
        Object.keys(data).forEach(key => {
          if (data[key] !== null && data[key] !== undefined) {
            if (imageFields.includes(key) && data[key]) {
              siteSettings[key] = mediaUrl(data[key]);
            } else {
              siteSettings[key] = data[key];
            }
          }
        });

        // Build combined address if not provided by API
        if (!siteSettings.address) {
          siteSettings.address = [
            siteSettings.address_line_1,
            siteSettings.address_line_2,
            siteSettings.city,
          ].filter(Boolean).join('\n');
        }

        // Build combined phone if not provided by API
        if (!siteSettings.phone) {
          siteSettings.phone = siteSettings.phone_primary || '';
        }

        // Map embed URL fallback
        if (!siteSettings.map_embed_url) {
          siteSettings.map_embed_url = siteSettings.google_maps_embed_url || '';
        }

        // Update page favicon dynamically
        if (siteSettings.favicon) {
          const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
          link.type = 'image/x-icon';
          link.rel = 'shortcut icon';
          link.href = siteSettings.favicon;
          document.head.appendChild(link);
        }

        // Update page title
        if (siteSettings.school_name) {
          document.title = siteSettings.school_name;
        }

        siteSettings.loaded = true;
        console.log('[Settings] Loaded successfully from API');
      }
    } catch (e) {
      console.warn('[Settings] Failed to load, using defaults:', e.message);
    }
    return siteSettings;
  })();

  return _promise;
}

export default siteSettings;
